// src/components/PainSlider.js
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { formatCurrency } from '../utils/formatting';

const PainSlider = ({
    amount,
    onComplete,
    onCancel,
    budgetCategories = [],
    preselectedCategory = null
}) => {
    const [painLevel, setPainLevel] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(preselectedCategory);
    const sliderRef = useRef(null);
    const sliderWidth = useRef(0);

    // Initialize slider width on mount
    useEffect(() => {
        if (sliderRef.current) {
            sliderWidth.current = sliderRef.current.offsetWidth;
        }
    }, []);

    // Handle mouse/touch down on the slider handle
    const handleDragStart = (e) => {
        setIsDragging(true);
        document.addEventListener('mousemove', handleDrag);
        document.addEventListener('mouseup', handleDragEnd);
        document.addEventListener('touchmove', handleDrag);
        document.addEventListener('touchend', handleDragEnd);

        // Prevent default to avoid text selection during drag
        e.preventDefault();
    };

    // Handle mouse/touch move
    const handleDrag = (e) => {
        if (!isDragging || !sliderRef.current) return;

        // Get mouse/touch position
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const rect = sliderRef.current.getBoundingClientRect();
        const x = clientX - rect.left;

        // Calculate pain level (0-10) based on position
        let newPainLevel = Math.round((x / sliderWidth.current) * 10);
        newPainLevel = Math.max(0, Math.min(10, newPainLevel));

        setPainLevel(newPainLevel);

        // When slider reaches the maximum, consider it completed
        if (newPainLevel === 10) {
            setIsCompleted(true);
            handleDragEnd();
        }
    };

    // Handle mouse/touch up
    const handleDragEnd = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleDrag);
        document.removeEventListener('mouseup', handleDragEnd);
        document.removeEventListener('touchmove', handleDrag);
        document.removeEventListener('touchend', handleDragEnd);

        // If completed, call onComplete
        if (isCompleted) {
            onComplete(painLevel, selectedCategory);
        }
    };

    // Handle direct click on the slider track
    const handleTrackClick = (e) => {
        if (!sliderRef.current) return;

        const rect = sliderRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;

        // Calculate pain level (0-10) based on click position
        let newPainLevel = Math.round((x / sliderWidth.current) * 10);
        newPainLevel = Math.max(0, Math.min(10, newPainLevel));

        setPainLevel(newPainLevel);

        // When clicking directly to maximum, consider it completed
        if (newPainLevel === 10) {
            setIsCompleted(true);
            setTimeout(() => {
                onComplete(newPainLevel, selectedCategory);
            }, 300);
        }
    };

    // Handle category selection
    const handleCategoryChange = (e) => {
        setSelectedCategory(e.target.value);
    };

    // Calculate position of the slider handle
    const handlePosition = `${(painLevel / 10) * 100}%`;

    // Dynamic colors based on pain level
    const trackColor = isCompleted
        ? 'bg-green-500'
        : painLevel < 4
            ? 'bg-red-500'
            : painLevel < 7
                ? 'bg-yellow-500'
                : 'bg-green-500';

    return (
        <div className="p-6 bg-white rounded-lg shadow-lg">
            <div className="mb-6 text-center">
                <h3 className="text-xl font-bold mb-2">
                    Acknowledge Your Spending
                </h3>
                <p className="text-gray-600">
                    Drag the slider all the way to the right to acknowledge that you spent {formatCurrency(amount)}
                </p>
            </div>

            {/* Pain slider */}
            <div className="mb-8">
                <div className="flex justify-between text-sm text-gray-500 mb-2">
                    <span>Less Pain</span>
                    <span>More Pain</span>
                </div>

                <div
                    ref={sliderRef}
                    className="h-10 bg-gray-200 rounded-full relative cursor-pointer"
                    onClick={handleTrackClick}
                >
                    {/* Filled track */}
                    <div
                        className={`absolute left-0 top-0 h-full rounded-full ${trackColor}`}
                        style={{ width: handlePosition }}
                    ></div>

                    {/* Slider handle */}
                    <div
                        className={`absolute top-0 h-10 w-10 bg-white rounded-full shadow-lg transform -translate-x-1/2 flex items-center justify-center border-2 ${isDragging ? 'border-blue-500' : 'border-gray-300'}`}
                        style={{ left: handlePosition }}
                        onMouseDown={handleDragStart}
                        onTouchStart={handleDragStart}
                    >
                        <span className="text-sm font-bold">{painLevel}</span>
                    </div>
                </div>

                <div className="text-center text-gray-700 mt-2">
                    {isCompleted
                        ? 'Thank you for acknowledging your spending!'
                        : `Pain level: ${painLevel} / 10`}
                </div>
            </div>

            {/* Budget category selection */}
            {budgetCategories && budgetCategories.length > 0 && (
                <div className="mb-6">
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                        Which budget category does this affect?
                    </label>
                    <select
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        value={selectedCategory || ''}
                        onChange={handleCategoryChange}
                    >
                        <option value="">Select a budget category</option>
                        {budgetCategories.map((category) => (
                            <option key={category.id} value={category.id}>
                                {category.group} - {category.name} ({formatCurrency(category.amount - category.spent)} remaining)
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {/* Actions */}
            <div className="flex justify-between">
                <button
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                    onClick={onCancel}
                >
                    Cancel
                </button>

                <button
                    className={`px-4 py-2 rounded ${isCompleted ? 'bg-green-500 hover:bg-green-600' : 'bg-blue-500 hover:bg-blue-600'} text-white`}
                    onClick={() => onComplete(painLevel, selectedCategory)}
                    disabled={painLevel < 10}
                >
                    {isCompleted ? 'Acknowledged' : 'Acknowledge Spending'}
                </button>
            </div>
        </div>
    );
};

PainSlider.propTypes = {
    amount: PropTypes.number.isRequired,
    onComplete: PropTypes.func.isRequired,
    onCancel: PropTypes.func,
    budgetCategories: PropTypes.array,
    preselectedCategory: PropTypes.string
};

export default PainSlider;